import { ChevronLeft, ChevronRight, ExternalLink, Eye, EyeOff, Filter, Flag, MapPin, Store, Trash2 } from "lucide-react";
import { useState, useEffect } from "react";
import BONavigation from "../../../components/admin/BONavigation";
import { getAdminReports, getAdminReportsNafStats, deleteEstablishmentReport, hideEstablishment, unhideEstablishment } from "../../../interfaces/establishments";
import { IEstablishmentReport, EstablishmentReportType, NafReportStat } from "../../../interfaces/establishment";
import { REPORT_LABELS, REPORT_COLORS } from "../../../utils/reportConstants";

const SYSTEM_USER_ID = "system:closing-checker";

const SIGNAL_TYPE_LABELS: Record<string, string> = {
    restaurant_opening: "Ouverture",
    franchise_signing: "Franchise",
    network_expansion: "Expansion",
    multi_site: "Multi-site",
    restaurant_owner_change: "Changement exploitant",
    restaurant_closing: "Fermeture",
    hygiene_control: "Contrôle sanitaire",
};

export default function AdminSignalReports() {
    const [reports, setReports] = useState<IEstablishmentReport[]>([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState<EstablishmentReportType | "">("");
    const [filterNaf, setFilterNaf] = useState("");
    const [showHidden, setShowHidden] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [hidingId, setHidingId] = useState<string | null>(null);
    const [nafStats, setNafStats] = useState<NafReportStat[]>([]);
    const limit = 50;

    const load = async (p: number, type: EstablishmentReportType | "", hidden: boolean, naf: string) => {
        setLoading(true);
        try {
            const data = await getAdminReports({
                page: p,
                limit,
                reportType: type || undefined,
                nafCode: naf || undefined,
                showHidden: hidden,
            });
            setReports(data.reports);
            setTotal(data.total);
            setTotalPages(data.totalPages);
        } catch { /* ignore */ }
        setLoading(false);
    };

    const loadNafStats = async (hidden: boolean) => {
        try {
            const stats = await getAdminReportsNafStats({ showHidden: hidden });
            setNafStats(stats);
        } catch { /* ignore */ }
    };

    useEffect(() => { load(page, filterType, showHidden, filterNaf); }, [page, filterType, showHidden, filterNaf]);
    useEffect(() => { loadNafStats(showHidden); }, [showHidden]);

    const handleDelete = async (report: IEstablishmentReport) => {
        if (!report._id) return;
        setDeletingId(report._id);
        try {
            await deleteEstablishmentReport(report.establishmentId, report.userId);
            setReports(prev => prev.filter(r => r._id !== report._id));
            setTotal(prev => prev - 1);
        } catch { /* ignore */ }
        setDeletingId(null);
    };

    const handleToggleHide = async (report: IEstablishmentReport) => {
        if (!report.establishmentId) return;
        const isHidden = report.isHidden ?? false;
        setHidingId(report.establishmentId);
        try {
            if (isHidden) {
                await unhideEstablishment(report.establishmentId);
            } else {
                await hideEstablishment(report.establishmentId);
            }
            setReports(prev =>
                prev.map(r =>
                    r.establishmentId === report.establishmentId
                        ? { ...r, isHidden: !isHidden }
                        : r
                )
            );
        } catch { /* ignore */ }
        setHidingId(null);
    };

    // Stats per type — from current page only (indicative)
    const typeCounts = reports.reduce<Record<string, number>>((acc, r) => {
        acc[r.reportType] = (acc[r.reportType] || 0) + 1;
        return acc;
    }, {});

    const formatDate = (d?: string) => {
        if (!d) return "—";
        return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
    };

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-6 py-6">
                <BONavigation />

                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-xl font-semibold text-gray-900">Signalements prospects</h1>
                        <p className="text-sm text-gray-500 mt-0.5">{total} signalement{total !== 1 ? "s" : ""}</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => { setShowHidden(v => !v); setPage(1); }}
                            className={`flex items-center gap-1.5 text-sm px-3 py-1.5 border rounded transition-colors ${
                                showHidden
                                    ? "border-orange-300 bg-orange-50 text-orange-600"
                                    : "border-gray-200 text-gray-500 hover:border-gray-300"
                            }`}
                        >
                            {showHidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                            {showHidden ? "Avec masqués" : "Sans masqués"}
                        </button>
                        <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4 text-gray-400" />
                            <select
                                value={filterType}
                                onChange={(e) => { setFilterType(e.target.value as EstablishmentReportType | ""); setPage(1); }}
                                className="text-sm border border-gray-200 rounded px-3 py-1.5 focus:outline-none focus:border-gray-400"
                            >
                                <option value="">Tous les types</option>
                                {(Object.entries(REPORT_LABELS) as [EstablishmentReportType, string][]).map(([type, label]) => (
                                    <option key={type} value={type}>{label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex items-center gap-2">
                            <select
                                value={filterNaf}
                                onChange={(e) => { setFilterNaf(e.target.value); setPage(1); }}
                                className="text-sm border border-gray-200 rounded px-3 py-1.5 focus:outline-none focus:border-gray-400"
                            >
                                <option value="">Tous les codes NAF</option>
                                {nafStats.map((s) => (
                                    <option key={s.nafCode} value={s.nafCode}>
                                        {s.nafCode}{s.description ? ` — ${s.description}` : ""} ({s.count})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Stats cards */}
                <div className="grid grid-cols-3 gap-3 mb-6 sm:grid-cols-6">
                    {(Object.entries(REPORT_LABELS) as [EstablishmentReportType, string][]).map(([type, label]) => (
                        <button
                            key={type}
                            onClick={() => { setFilterType(filterType === type ? "" : type); setPage(1); }}
                            className={`border rounded-lg p-3 text-left transition-colors ${
                                filterType === type
                                    ? REPORT_COLORS[type]
                                    : "border-gray-200 hover:border-gray-300"
                            }`}
                        >
                            <div className="text-2xl font-semibold text-gray-900">{typeCounts[type] || 0}</div>
                            <div className="text-xs text-gray-500 mt-0.5">{label}</div>
                        </button>
                    ))}
                </div>

                {/* Table */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-white">
                            <tr>
                                <th className="px-4 py-3 w-16"></th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Établissement</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Code NAF</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Signal</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Type de signalement</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Note</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Signalé par</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Date</th>
                                <th className="px-4 py-3 w-10"></th>
                                <th className="px-4 py-3 w-10"></th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                            {loading ? (
                                <tr>
                                    <td colSpan={10} className="px-4 py-12 text-center text-sm text-gray-400">
                                        Chargement…
                                    </td>
                                </tr>
                            ) : reports.length === 0 ? (
                                <tr>
                                    <td colSpan={10} className="px-4 py-12 text-center">
                                        <Flag className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                                        <p className="text-sm text-gray-400">Aucun signalement</p>
                                    </td>
                                </tr>
                            ) : reports.map((report) => {
                                const isHidden = report.isHidden ?? false;
                                return (
                                <tr key={report._id} className={`hover:bg-[#F9F6F3] ${isHidden ? "opacity-50" : ""}`}>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-1">
                                            <a
                                                href={`/establishments/${report.establishmentId}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title="Ouvrir la fiche établissement"
                                                className="text-gray-300 hover:text-gray-600 transition-colors"
                                            >
                                                <Store className="w-4 h-4" />
                                            </a>
                                            {report.siren && (
                                                <a
                                                    href={`https://www.pappers.fr/entreprise/${report.siren}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    title="Ouvrir sur Pappers"
                                                    className="text-gray-300 hover:text-blue-500 transition-colors"
                                                >
                                                    <ExternalLink className="w-4 h-4" />
                                                </a>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            <div>
                                                <div className={`text-sm font-medium ${isHidden ? "line-through text-gray-400" : "text-gray-900"}`}>
                                                    {report.establishmentName || "—"}
                                                    {isHidden && <span className="ml-2 text-xs font-normal text-orange-400 no-underline" style={{textDecoration: "none"}}>masqué</span>}
                                                </div>
                                                {report.establishmentCity && (
                                                    <div className="flex items-center gap-1 text-xs text-gray-400">
                                                        <MapPin className="w-3 h-3" />
                                                        {report.establishmentCity}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        {report.nafCode ? (
                                            <div>
                                                <div className="text-xs font-medium text-gray-700">{report.nafCode}</div>
                                                {report.nafDescription && (
                                                    <div className="text-xs text-gray-400 max-w-[180px] truncate">{report.nafDescription}</div>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-xs text-gray-300">—</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        {report.signalType ? (
                                            <span className="text-xs text-gray-600">
                                                {SIGNAL_TYPE_LABELS[report.signalType] || report.signalType}
                                            </span>
                                        ) : (
                                            <span className="text-xs text-gray-300">—</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded border ${REPORT_COLORS[report.reportType]}`}>
                                            <Flag className="w-3 h-3" />
                                            {REPORT_LABELS[report.reportType]}
                                        </span>
                                        {report.source === "api" && (
                                            <span title="Envoyé via l'API publique" className="ml-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200">
                                                API
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className="text-xs text-gray-600 max-w-[160px] truncate block">
                                            {report.comment || <span className="text-gray-300">—</span>}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className="text-xs text-gray-600">
                                            {report.userId === SYSTEM_USER_ID || (!report.userName && !report.userEmail)
                                                ? <span className="text-gray-400 italic">System auto detect</span>
                                                : report.userName || report.userEmail
                                            }
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className="text-xs text-gray-400">{formatDate(report.createdAt)}</span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <button
                                            onClick={() => handleToggleHide(report)}
                                            disabled={hidingId === report.establishmentId}
                                            title={isHidden ? "Rendre visible l'établissement" : "Masquer l'établissement"}
                                            className={`transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                                                isHidden
                                                    ? "text-orange-500 hover:text-gray-400"
                                                    : "text-gray-300 hover:text-orange-500"
                                            }`}
                                        >
                                            {isHidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                        </button>
                                    </td>
                                    <td className="px-4 py-3">
                                        <button
                                            onClick={() => handleDelete(report)}
                                            disabled={deletingId === report._id}
                                            className="text-gray-300 hover:text-red-500 transition-colors"
                                            title="Supprimer le signalement"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                        <span className="text-sm text-gray-500">
                            Page {page} / {totalPages}
                        </span>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="p-1.5 border border-gray-200 rounded hover:bg-[#F9F6F3] disabled:opacity-40"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                                className="p-1.5 border border-gray-200 rounded hover:bg-[#F9F6F3] disabled:opacity-40"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
